import { Component, EventBus } from '../core/smpl-components.js'
import { Events } from '../core/events'
import { MqlMatchMedia } from '../utils/mql-match-media'

import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

/**
 * ScrollParallax
 * Adds scroll-linked parallax motion to elements with data-speed.
 *
 * How it works:
 * - Creates one GSAP ScrollTrigger tween per matching element.
 * - Reads data-speed to control movement strength and direction.
 * - Supports per-element anchor with data-speed-anchor:
 *   - neutral (default): element crosses its natural position halfway through the trigger range
 *   - start: natural position at trigger start
 *   - end: natural position at trigger end
 * - Supports a separate move target with data-parallax-target:
 *   - value is a CSS selector resolved inside the marked element
 *   - the marked element stays the scroll trigger; the resolved child is what moves
 *   - falls back to the marked element itself when absent or unmatched
 *
 * Lifecycle:
 * - init(): find elements and create tweens
 * - refresh(): refresh ScrollTrigger calculations (e.g. after resize/content changes)
 * - cleanup()/destroy(): kill all tweens and triggers to prevent leaks
 */
class ScrollParallax {
	constructor(options) {
		this.options = {
			selector: '[data-speed]',
			scroller: null,
			scrub: 0.1,
			start: 'top bottom',
			end: 'bottom top',
			distanceFactor: -0.5,
			defaultAnchor: 'neutral',
			// Transforms must go on teardown: the component destroys the instance
			// below the MEDIUM breakpoint, and a leftover translateY would stay
			// baked on every data-speed element for the rest of the page's life.
			clearTransformOnDestroy: true,
			...options,
		}

		this.selector = this.options.selector
		this.scroller = this.options.scroller
		this.items = []

		this.init()
	}

	getAnchor(el) {
		const anchor = (el.getAttribute('data-speed-anchor') || this.options.defaultAnchor || 'neutral').toLowerCase()
		if (anchor === 'start' || anchor === 'end' || anchor === 'neutral' || anchor === 'center') {
			return anchor === 'center' ? 'neutral' : anchor
		}
		return 'neutral'
	}

	getTweenValues(el, speed) {
		const distance = window.innerHeight * this.options.distanceFactor * speed
		const anchor = this.getAnchor(el)

		if (anchor === 'neutral') {
			const half = distance * 0.5
			return { fromY: -half, toY: half }
		}

		if (anchor === 'start') {
			return { fromY: 0, toY: distance }
		}

		return { fromY: -distance, toY: 0 }
	}

	getTarget(el) {
		const selector = (el.getAttribute('data-parallax-target') || '').trim()
		if (!selector) return el
		return el.querySelector(selector) || el
	}

	init() {
		this.cleanup()

		const targets = gsap.utils.toArray(this.selector)
		if (!targets.length) return

		this.items = targets.map((el) => {
			const raw = parseFloat(el.getAttribute('data-speed'))
			const speed = Number.isFinite(raw) ? raw : 1
			const target = this.getTarget(el)

			const tween = gsap.fromTo(
				target,
				{
					y: () => this.getTweenValues(el, speed).fromY,
				},
				{
					y: () => this.getTweenValues(el, speed).toY,
					ease: 'none',
					overwrite: 'auto',
					immediateRender: false,
					scrollTrigger: {
						trigger: el,
						scroller: this.scroller,
						start: this.options.start,
						end: this.options.end,
						scrub: this.options.scrub,
						invalidateOnRefresh: true,
					},
				},
			)

			return { el, target, speed, tween }
		})
	}

	refresh() {
		ScrollTrigger.refresh()
	}

	cleanup() {
		if (!this.items.length) return

		this.items.forEach(({ el, target, tween }) => {
			if (!tween) return
			if (tween.scrollTrigger) tween.scrollTrigger.kill()
			tween.kill()
			if (this.options.clearTransformOnDestroy) {
				gsap.set(target || el, { clearProps: 'transform' })
			}
		})

		this.items = []
	}

	destroy() {
		this.cleanup()
	}
}

export default class extends Component {
	prepare() {
		this.bndOnContentRefresh = this.onContentRefresh.bind(this)
		this.bndOnContentReplace = this.onContentReplace.bind(this)
		this.bndOnBreakpointChange = this.onBreakpointChange.bind(this)

		EventBus.on(Events.SWUP_CONTENT_REPLACE, this.bndOnContentReplace)
		EventBus.on(Events.PAGE_RESIZE, this.bndOnContentRefresh)
	}

	mount() {
		this.parallaxEnabled = false

		this.matchMedia = new MqlMatchMedia(`(min-width:${window.app.breakpoints.MEDIUM}px)`, this.bndOnBreakpointChange)
	}

	unmount() {
		if (this.matchMedia) {
			this.matchMedia.destroy()
			this.matchMedia = null
		}

		EventBus.off(Events.SWUP_CONTENT_REPLACE, this.bndOnContentReplace)
		EventBus.off(Events.PAGE_RESIZE, this.bndOnContentRefresh)

		if (this.scrollParallax) {
			this.scrollParallax.destroy()
			this.scrollParallax = null
		}
	}

	onBreakpointChange(match) {
		this.parallaxEnabled = !!match

		if (!this.parallaxEnabled) {
			if (this.scrollParallax) {
				this.scrollParallax.destroy()
				this.scrollParallax = null
			}
			return
		}

		if (!this.scrollParallax) {
			this.scrollParallax = new ScrollParallax({})
		} else {
			this.scrollParallax.init()
		}
	}

	onContentRefresh() {
		if (this.parallaxEnabled && this.scrollParallax) {
			this.scrollParallax.refresh()
		}
	}

	onContentReplace() {
		if (!this.parallaxEnabled || !this.scrollParallax) return

		// Keep same delayed re-init pattern as ScrollReveal for swup timing.
		setTimeout(() => {
			if (this.scrollParallax) {
				this.scrollParallax.init()
			}
		}, 100)
	}
}
